// Instanced scatter props — trees, bushes, rocks, grass, cacti on terrain hexes.
// One InstancedMesh per (config, prop type, sub-mesh). Everything is derived
// from the hex (q, r) and world position, so a map always looks the same
// across reloads and frames (no jitter).
//
// Variation (pattern shared with WorldForge's 3D fidelity work):
// - species: a config lists weighted prop types (a forest mixes pine, spruce,
//   oak, birch and bush); each instance picks one by a position hash;
// - colour: a low-frequency world-space noise field picks along each species'
//   palette, so neighbouring trees drift together into groves instead of
//   flickering like confetti, plus a small per-instance jitter;
// - shape: independent height and width scale, and a slight lean;
// - texture: baked per-face tone + vertical gradient (TerrainScatterMaterials).
//
// Batching: fixed-offset child geometries have their local transforms baked
// into vertex positions at module load, so per-instance matrices only carry
// world placement.

import type { SceneNode } from '@aod/protocol';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { type ShadowSpot, ShadowSpots } from './ContactShadows.js';
import {
  PROP_SUBMESHES,
  type ScatterConfig,
  type ScatterPropType,
  windTime,
} from './TerrainScatterMaterials.js';
import { hexNoise } from './elevation.js';
import {
  SCATTER_CONFIGS,
  SHADOW_RADIUS,
  type ScatterInstance,
  buildScatterInstances,
  configAppliesToClimate,
  pointInHex,
} from './scatterInstances.js';

export { buildScatterInstances, pointInHex, SCATTER_CONFIGS, SHADOW_RADIUS };
export type { ScatterInstance } from './scatterInstances.js';
export type { ScatterConfig } from './TerrainScatterMaterials.js';
export type { ScatterPropType } from './TerrainScatterMaterials.js';

interface TerrainScatterProps {
  readonly tiles: ReadonlyArray<SceneNode>;
  /** "q,r" keys where trees are cleared (settlements); grass and rocks stay. */
  readonly clearCoords?: ReadonlySet<string>;
  /** 1-based day of year for the season tint; 0/undefined = season off. */
  readonly seasonDay?: number;
  /** Density tier (1 = normal, 2 = Large Board: ~1.6x instances). */
  readonly detail?: number;
  /** Map climate: selects the jungle scatter on tropical maps (default temperate). */
  readonly climate?: string;
}
// Module-level scratch objects reused across instance updates.
const _obj = new THREE.Object3D();
const _color = new THREE.Color();

function PropGroup({
  propType,
  instances,
  castShadow,
}: {
  propType: ScatterPropType;
  instances: ScatterInstance[];
  castShadow: boolean;
}) {
  const subMeshes = PROP_SUBMESHES[propType];
  const meshRefs = useRef<Array<THREE.InstancedMesh | null>>([]);

  // Contact shadow: one blob per instance, derived from the same instances
  // as the props above so they can never drift apart. Grass has none.
  const shadowSpots = useMemo<ShadowSpot[]>(() => {
    const base = SHADOW_RADIUS[propType];
    if (base === undefined) return [];
    return instances.map((inst) => ({
      x: inst.x,
      y: inst.y + 0.03,
      z: inst.z,
      d: base * 2 * ((inst.scaleX + inst.scaleZ) / 2),
      strength: 0.55 + hexNoise(inst.x * 3.3, inst.z * 2.9) * 0.25,
    }));
  }, [instances, propType]);

  useEffect(() => {
    for (let s = 0; s < subMeshes.length; s++) {
      const mesh = meshRefs.current[s];
      if (!mesh) continue;
      const role = subMeshes[s]!.role;

      for (let i = 0; i < instances.length; i++) {
        const inst = instances[i]!;
        _obj.position.set(inst.x, inst.y, inst.z);
        _obj.rotation.set(inst.tiltX, inst.rotY, inst.tiltZ);
        _obj.scale.set(inst.scaleX, inst.scaleY, inst.scaleZ);
        _obj.updateMatrix();
        mesh.setMatrixAt(i, _obj.matrix);

        const rgb = role === 'trunk' ? inst.trunkColor : inst.mainColor;
        if (rgb) mesh.setColorAt(i, _color.setRGB(rgb[0], rgb[1], rgb[2]));
      }

      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.computeBoundingSphere();
    }
  }, [instances, subMeshes]);

  return (
    <group>
      {subMeshes.map((sub, s) => (
        <instancedMesh
          name={`scatter-${propType}`}
          key={`${propType}-${s}`}
          ref={(m) => {
            meshRefs.current[s] = m;
          }}
          args={[sub.geometry, sub.material, instances.length]}
          castShadow={castShadow}
        />
      ))}
      <ShadowSpots spots={shadowSpots} />
    </group>
  );
}

function ScatterGroup({
  tiles,
  config,
  clearCoords,
  seasonDay,
  detail,
}: {
  tiles: ReadonlyArray<SceneNode>;
  config: ScatterConfig;
  clearCoords?: ReadonlySet<string>;
  seasonDay: number;
  detail: number;
}) {
  // buildScatterInstances only reads static-per-match tile fields (q, r,
  // biome, position) plus the clears/season/detail inputs. The snapshot's
  // tiles array is fresh on every publish, so keying the memo on it rebuilt
  // every scatter instance 40x/s at 4x speed for identical output. Key on a
  // content signature instead (same idiom as Scene's settlementKey).
  const terrainKey = useMemo(
    () =>
      tiles
        .map((t) => {
          const p = t.props as Record<string, unknown> | undefined;
          const [x, , z] = t.transform.position;
          return `${p?.q ?? 0},${p?.r ?? 0},${p?.biome ?? 'plains'},${x},${z},${p?.level ?? ''},${p?.volcano ?? ''}`;
        })
        .join('|'),
    [tiles],
  );
  const byProp = useMemo(
    () => buildScatterInstances(tiles, config, clearCoords, seasonDay, detail),
    [terrainKey, config, clearCoords, seasonDay, detail],
  );
  return (
    <>
      {Array.from(byProp.entries()).map(([propType, instances]) => (
        <PropGroup
          key={propType}
          propType={propType}
          instances={instances}
          castShadow={config.castShadow}
        />
      ))}
    </>
  );
}

export function TerrainScatter({
  tiles,
  clearCoords,
  seasonDay = 0,
  detail = 1,
  climate,
}: TerrainScatterProps) {
  return (
    <>
      {SCATTER_CONFIGS.filter((config) => configAppliesToClimate(config, climate)).map((config) => (
        <ScatterGroup
          key={config.id}
          tiles={tiles}
          config={config}
          clearCoords={clearCoords}
          seasonDay={seasonDay}
          detail={detail}
        />
      ))}
    </>
  );
}

/** Advances the shared wind-sway uniform (TerrainScatterMaterials). Must be in the scene. */
export function WindAnimator() {
  useFrame((_, delta) => {
    windTime.value += delta;
  });
  return null;
}
